/*
  COMPONENT: Predictive Search Runtime

  Owns:
  - Predictive search requests
  - Predictive search result replacement

  Does not own:
  - Main search results page rendering
*/

(() => {
  const parser = new DOMParser();
  const delay = 75;

  function debounce(callback) {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), delay);
    };
  }

  async function fetchResults(query, search, signal) {
    const base = window.CurtisTheme?.routes?.predictiveSearch || '/search/suggest';
    const url = new URL(base, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', search.dataset.predictiveSearchTypes || 'product,collection,page,article');
    url.searchParams.set('resources[limit]', search.dataset.predictiveSearchLimit || '6');
    url.searchParams.set('section_id', 'predictive-search');

    const response = await fetch(url.toString(), { credentials: 'same-origin', signal });
    if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);

    const html = await response.text();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.querySelector('[data-predictive-search-results]')?.innerHTML || '';
  }

  function initializePredictiveSearch(root = document) {
    root.querySelectorAll('[data-predictive-search]').forEach((search) => {
      if (search.dataset.initialized === 'true') return;
      search.dataset.initialized = 'true';

      const input = search.querySelector('[data-predictive-search-input]');
      const surface = search.closest('[data-search-surface]') || search;
      const results = search.querySelector('[data-predictive-search-results]') || surface.querySelector('[data-predictive-search-results]');
      const discovery = surface.querySelector('[data-search-discovery]');
      const clearButtons = surface.querySelectorAll('[data-search-clear]');
      const status = search.querySelector('[data-predictive-search-status]') || surface.querySelector('[data-predictive-search-status]');
      if (!input || !results) return;

      let requestCounter = 0;
      let requestController = null;
      let activeIndex = -1;

      const options = () => Array.from(results.querySelectorAll('a[href]'));

      const clearActiveOption = () => {
        options().forEach((option) => {
          option.classList.remove('is-active');
          option.setAttribute('aria-selected', 'false');
        });
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
      };

      const setActiveOption = (index) => {
        const availableOptions = options();
        if (!availableOptions.length) return;

        clearActiveOption();
        activeIndex = (index + availableOptions.length) % availableOptions.length;
        const activeOption = availableOptions[activeIndex];
        activeOption.classList.add('is-active');
        activeOption.setAttribute('aria-selected', 'true');
        input.setAttribute('aria-activedescendant', activeOption.id);
        activeOption.scrollIntoView({ block: 'nearest' });
      };

      const prepareOptions = () => {
        options().forEach((option, index) => {
          option.id = `${input.id}-suggestion-${index}`;
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', 'false');
          option.tabIndex = -1;
        });
        clearActiveOption();
      };

      const syncChrome = () => {
        const hasValue = input.value.trim().length > 0;
        clearButtons.forEach((button) => {
          button.hidden = !hasValue;
        });
      };

      const showDiscovery = () => {
        results.innerHTML = '';
        results.hidden = true;
        results.removeAttribute('aria-busy');
        input.setAttribute('aria-expanded', 'false');
        clearActiveOption();
        if (status) status.textContent = '';
        if (discovery) discovery.hidden = false;
      };

      const showError = () => {
        const message = search.dataset.predictiveSearchErrorMessage || '';
        const error = document.createElement('div');
        const copy = document.createElement('p');

        error.className = 'predictive-search-results__empty';
        error.setAttribute('role', 'alert');
        copy.textContent = message;
        error.append(copy);
        results.replaceChildren(error);
        results.hidden = false;
        results.removeAttribute('aria-busy');
        input.setAttribute('aria-expanded', 'true');
        clearActiveOption();
        if (status) status.textContent = message;
        if (discovery) discovery.hidden = false;
      };

      const update = debounce(async () => {
        const query = input.value.trim();
        syncChrome();

        if (query.length < 2) {
          requestCounter += 1;
          requestController?.abort();
          showDiscovery();
          return;
        }

        const requestId = ++requestCounter;
        requestController?.abort();
        requestController = new AbortController();
        if (discovery) discovery.hidden = true;
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        results.setAttribute('aria-busy', 'true');
        results.innerHTML = '';

        try {
          const html = await fetchResults(query, search, requestController.signal);
          if (requestId !== requestCounter) return;
          results.innerHTML = html;
          prepareOptions();
          if (status) {
            status.textContent = (search.dataset.predictiveSearchStatusTemplate || '__count__ suggested results available').replace(
              '__count__',
              String(options().length)
            );
          }
        } catch (error) {
          if (error.name === 'AbortError' || requestId !== requestCounter) return;
          showError();
        } finally {
          if (requestId === requestCounter) results.removeAttribute('aria-busy');
        }
      });

      input.addEventListener('input', update);
      input.addEventListener('search', update);
      input.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          if (!results.hidden && options().length) {
            event.preventDefault();
            setActiveOption(activeIndex + 1);
          }
        } else if (event.key === 'ArrowUp') {
          if (!results.hidden && options().length) {
            event.preventDefault();
            setActiveOption(activeIndex - 1);
          }
        } else if (event.key === 'Enter' && activeIndex >= 0) {
          event.preventDefault();
          options()[activeIndex]?.click();
        } else if (event.key === 'Escape' && !results.hidden) {
          event.preventDefault();
          event.stopPropagation();
          showDiscovery();
        }
      });
      clearButtons.forEach((button) => {
        button.addEventListener('click', () => {
          input.value = '';
          syncChrome();
          showDiscovery();
          input.focus();
        });
      });
      window.CurtisTheme?.registerCleanup?.(search, () => requestController?.abort());
      syncChrome();
    });
  }

  initializePredictiveSearch();
  document.addEventListener('shopify:section:load', (event) => initializePredictiveSearch(event.target));
})();
